/** In-memory record of the most recent local or remote recognition attempt,
 * shown in the segment dialog for debugging extraction problems. */

interface FileMeta {
  name: string;
  type: string;
  size: number;
}

export interface LlmExchange {
  provider: string;
  model: string;
  /** All images sent with the request (empty = note only). */
  files?: FileMeta[];
  /** Legacy single-file field (exchanges stored before multi-image). */
  file?: FileMeta | null;
  note: string;
  startedAt: number;
  durationMs?: number;
  /** Request body with the file payload elided (it would be megabytes of base64). */
  request?: unknown;
  /** e.g. `HTTP 200` or `ApiError 400`. */
  status?: string;
  rawResponse?: string;
  legs?: unknown;
  /** Local extraction produced useful fields but could not fill every core field. */
  partial?: boolean;
  warning?: string;
  error?: string;
}

let last: LlmExchange | null = null;

/** Start recording a new exchange (replaces the previous one). The extractor
 * keeps mutating the returned object as the exchange progresses. */
export function beginExchange(x: LlmExchange): LlmExchange {
  last = x;
  return x;
}

export const lastExchange = (): LlmExchange | null => last;

/** Render the exchange as plain text for the debug tab. */
export function formatExchange(x: LlmExchange | null): string {
  if (!x) {
    return 'No recognition attempt in this session yet.\nAttach a screenshot (or write a note) and press Recognise.';
  }
  const files = x.files ?? (x.file ? [x.file] : []);
  const lines = [
    `Recognizer: ${x.provider} ${x.model}`,
    files.length
      ? `Files: ${files.map((f) => `${f.name || 'image'} (${f.type || 'unknown type'}, ${(f.size / 1024).toFixed(1)} KB)`).join(', ')}`
      : 'Files: (none — note only)',
    `Note: ${x.note.trim() || '(none)'}`,
    `When: ${new Date(x.startedAt).toLocaleString()}${x.durationMs != null ? ` · took ${(x.durationMs / 1000).toFixed(1)} s` : ''}`,
    `Status: ${x.status ?? '—'}`,
  ];
  if (x.warning) lines.push('', '--- Warning ---', x.warning);
  if (x.error) lines.push('', '--- Error ---', x.error);
  if (x.request !== undefined) lines.push('', '--- Request (file payload elided) ---', JSON.stringify(x.request, null, 2));
  if (x.rawResponse) lines.push('', '--- Raw response ---', x.rawResponse);
  if (x.legs !== undefined) lines.push('', '--- Parsed legs ---', JSON.stringify(x.legs, null, 2));
  return lines.join('\n');
}
